import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AnimatedHero from '../components/ui/AnimatedHero';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionGrid = motion(Grid);

const ClientsPage = () => {
  const { t } = useTranslation();

  // Partner logos (same as in PartnersCarousel)
  const clients = [
    { id: 1, name: 'Partner', path: 'partnerLogos/Partner_logo.svg' },
    { id: 2, name: 'Sport1', path: 'partnerLogos/sport1_logo.svg' },
    { id: 3, name: 'Walla', path: 'partnerLogos/Walla2024.svg' },
    { id: 4, name: 'Mako Keshet', path: 'partnerLogos/Mako_keshet_2024.svg' },
    { id: 5, name: '012 Mobile', path: 'partnerLogos/012_Mobile_Logo.svg' },
    { id: 6, name: 'Eco 99FM', path: 'partnerLogos/Eco_99_fm.svg' },
    { id: 7, name: 'Celcom', path: 'partnerLogos/celcom_2022.svg' },
    { id: 8, name: 'Toto', path: 'partnerLogos/toto_logo.svg' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Box>
      {/* Hero Section */}
      <AnimatedHero 
        title={t('clients.title')}
        subtitle={t('clients.subtitle')}
        showCta={false}
      />

      {/* Clients Grid Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <MotionTypography
              component="h2"
              variant="h3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              sx={{
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontWeight: 700,
                color: 'var(--text-primary)',
                mb: 2,
              }}
            >
              {t('clients.companies')}
            </MotionTypography>
            <MotionBox
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{
                height: '3px',
                width: '60px',
                background: 'var(--gradient-primary)',
                borderRadius: 'var(--radius-full)',
                mx: 'auto',
                mb: 3,
              }}
            />
            <MotionTypography
              variant="body1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              sx={{
                color: 'var(--text-secondary)',
                fontSize: '1.1rem',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              {t('clients.description')}
            </MotionTypography>
          </Box>

          {/* Clients Grid */}
          <MotionGrid
            container
            spacing={{ xs: 3, sm: 4, md: 5 }}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {clients.map((client) => (
              <MotionGrid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={client.id}
                variants={itemVariants}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    background: 'var(--color-white)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-sm)',
                    height: '200px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'var(--color-blue-light)',
                      boxShadow: 'var(--shadow-lg)',
                      '& img': {
                        filter: 'grayscale(0%)',
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <img
                      src={`/${client.path}`}
                      alt={client.name}
                      style={{
                        height: '80px',
                        width: 'auto',
                        maxWidth: '90%',
                        objectFit: 'contain',
                        filter: 'grayscale(30%)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              </MotionGrid>
            ))}
          </MotionGrid>

          {/* Call to Action */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            sx={{
              textAlign: 'center',
              mt: 10,
              pt: 6,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'var(--text-primary)',
                fontWeight: 600,
                mb: 2,
              }}
            >
              {t('clients.cta_title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
              }}
            >
              {t('clients.cta_message')}
            </Typography>
          </MotionBox>
        </Container>
      </Box>
    </Box>
  );
};

export default ClientsPage;
